/**
 * SubscribeToLiveMessagesUseCase
 *
 * Live-update use case for a live session's chat messages — the realtime half
 * the Task-7 `useLiveStreamingChat` hook calls. A thin wrapper over
 * `LiveStreamingService.subscribeToLiveMessages` that fans each delivered change
 * out to a per-change-type handler (`onAdded` / `onModified` / `onRemoved`) and
 * returns the `Unsubscribe` handle the caller invokes on cleanup. A live chat
 * message is the SAME `CommunityChatMessage` model as a community-wall message,
 * so this mirrors `SubscribeToCommunityMessagesUseCase` exactly (and reuses its
 * shared `CommunityMessageChangeHandlers` bundle — the hook can pass the same
 * `useCommunityLiveListener` handlers it uses for the wall).
 *
 * Flutter source: lib/presentation/screens/community/live_streaming_screen.dart:622-702
 *   (`subscribeToLiveMessages`).
 *   - Query: the `live_messages` collection-group filtered by
 *     `parentId == widget.liveStreamingMessageId` +
 *     `createdTimestamp > timestampChatObservation`, `.snapshots()` — handled by
 *     `LiveStreamingService.subscribeToLiveMessages` / `firestoreService`.
 *   - The live cursor (`timestampChatObservation`) is the most-recent message's
 *     `createdTimestamp`, or `DateTime.now().millisecondsSinceEpoch` when the
 *     session is empty (live_streaming_screen.dart:623-626). That fallback is the
 *     caller's responsibility — this use case forwards whatever `sinceTimestamp`
 *     it is handed.
 *   - live_streaming_screen.dart:635-701 — for each `docChange`, the doc is
 *     parsed and routed by `DocumentChangeType.added` / `.modified` / `.removed`.
 *     The presentation-side concerns (insert at index 0, scroll, unread pill,
 *     app-lifecycle gating, the optimistic-echo id-dedupe) stay in the feature
 *     hook on web — this use case is intentionally a thin domain pipe: route per
 *     change type, nothing more.
 *
 * A live session's messages are NOT end-to-end encrypted, so — unlike
 * `SubscribeToChatUpdatesUseCase` — there is no E2EE decryption fan-out here
 * (mirroring `SubscribeToCommunityMessagesUseCase`).
 *
 * Pure-domain contract (conventions.md § Clean Architecture flow):
 *   - Calls `LiveStreamingService.subscribeToLiveMessages`; does NOT touch
 *     `firestoreService` directly and does NOT dispatch to Redux.
 */

import type { Unsubscribe } from 'firebase/firestore';
import { LiveStreamingService } from '@/domain/services/liveStreaming/liveStreamingService';
import type { CommunityMessageChangeHandlers } from '@/domain/services/community/communityService';

export class SubscribeToLiveMessagesUseCase {
  private liveStreamingService: LiveStreamingService;

  constructor(liveStreamingService: LiveStreamingService = LiveStreamingService.getInstance()) {
    this.liveStreamingService = liveStreamingService;
  }

  /**
   * Subscribe to a live session's chat message changes arriving after
   * `sinceTimestamp`. Each change is routed to the matching per-change-type
   * handler.
   *
   * @param liveStreamingMessageId - The live streaming message id (`parentId` of
   *   every live message in the session — the collection-group equality filter,
   *   live_streaming_screen.dart:627-631).
   * @param sinceTimestamp - Lower-exclusive cursor; the snapshot does NOT
   *   re-deliver any doc with `createdTimestamp <= sinceTimestamp`
   *   (`isGreaterThan`, live_streaming_screen.dart:632). The caller supplies the
   *   most-recent message's `createdTimestamp`, or `Date.now()` when the session
   *   is empty (live_streaming_screen.dart:623-626).
   * @param handlers - Per-change-type handlers.
   * @returns The Firestore `Unsubscribe` handle produced by the service. The
   *   caller (the feature hook) is responsible for invoking it on cleanup.
   */
  execute(
    liveStreamingMessageId: string,
    sinceTimestamp: number,
    handlers: CommunityMessageChangeHandlers,
  ): Unsubscribe {
    return this.liveStreamingService.subscribeToLiveMessages(
      liveStreamingMessageId,
      sinceTimestamp,
      handlers,
    );
  }
}

export const subscribeToLiveMessagesUseCase = new SubscribeToLiveMessagesUseCase();
